import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleCheck } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Insight, InsightQueue, useInsightsApi } from '../../../../common/hooks/use-insights-api.hook';
import { VERDICT_DOT, VERDICT_TONE, provenance, verdictText } from '../insight-presentation';
import { InsightCard } from './InsightCard';
import { InsightRow } from './InsightRow';
import { useAgentWorkSignal } from '../../work/agent-work.store';
import { SidePanel } from '../../../../components/design-system/common/SidePanel';

interface Props {
    limit?: number;
    showSeeAll?: boolean;
    /** Pre-loaded queue, for stories and tests: with one, nothing is fetched. */
    seed?: InsightQueue;
    layout?: 'row' | 'grid';
    /** The queue as loaded — Today reads the verdict from it rather than fetching twice. */
    onQueue?: (queue: InsightQueue) => void;
}

/**
 * The action queue: what needs this vendor today, most important first.
 *
 * A strip on the dashboard, a grid on its own page. When the queue cannot be
 * reached the panel renders nothing at all — a broken widget on the dashboard
 * is worse than no widget.
 */
export const InsightsPanel: React.FC<Props> = ({
    limit = 4,
    showSeeAll = true,
    seed,
    layout = 'row',
    onQueue,
}) => {
    const { t } = useTranslation();
    const api = useInsightsApi();
    const isRow = layout === 'row';

    const [queue, setQueue] = useState<InsightQueue | null>(seed ?? null);
    const [loading, setLoading] = useState(!seed);
    const [failed, setFailed] = useState(false);
    const [busyId, setBusyId] = useState<string | null>(null);
    /* "See all" opens the rest of the queue in a drawer rather than on a route.
       The findings refer to the screen behind them, and a page swapped that
       context away for the same list at a different length. */
    const [allOpen, setAllOpen] = useState(false);
    /** The finding a row opened, shown in the drawer. */
    const [opened, setOpened] = useState<Insight | null>(null);

    const load = useCallback(async () => {
        if (seed) return;
        try {
            setFailed(false);
            const next = await api.getQueue({ limit });
            setQueue(next);
            onQueue?.(next);
        } catch {
            setFailed(true);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [limit, seed]);

    useEffect(() => {
        load();
    }, [load]);

    /* The analyst writes its findings as it finishes, so the queue is asked
       again rather than waiting for the next time this page is opened. */
    const finishedAt = useAgentWorkSignal((s) => s.finishedAt);
    useEffect(() => {
        if (finishedAt) void load();
    }, [finishedAt, load]);

    /**
     * Answered on the card. The server recomputed the gaps when the profile
     * was written, so drop the card now and reload: closing "no website" is
     * what opens "read your brand".
     */
    const resolved = async (findingId: string) => {
        setQueue((prev) => (prev ? { ...prev, items: prev.items.filter((i) => i.findingId !== findingId) } : prev));
        if (!seed) await load();
    };

    const dismiss = async (findingId: string) => {
        setBusyId(findingId);
        try {
            if (!seed) await api.acknowledge(findingId);
            setQueue((prev) => (prev ? { ...prev, items: prev.items.filter((i) => i.findingId !== findingId) } : prev));
        } catch {
            await load();
        } finally {
            setBusyId(null);
        }
    };

    if (failed) return null;

    const items = queue?.items ?? [];
    const total = (queue?.counts?.critical ?? 0) + (queue?.counts?.warning ?? 0) + (queue?.counts?.info ?? 0);
    const prov = provenance(items, queue?.detectors.length ?? 4);

    return (
        <section className="flex h-full flex-col gap-3">
            {/*
              The verdict is the heading.
             
              "Needs your attention" said nothing: it labelled a list that is
              plainly a list, above a sentence that actually says what is going
              on. The engine composes that sentence from the very rows
              underneath, so it cannot disagree with them — and a section that
              opens with "your search visibility is slipping" is worth reading
              in a way that a category name never is.
            */}
            {/*
              The banner runs the full width, and the count and "see all" ride
              inside it. Sitting them alongside made the verdict share its line
              with two controls, which cut the one sentence on this screen that
              is worth reading down to two thirds of the space.
            */}
            {!loading && items.length > 0 && queue?.verdict ? (
                <div className={cn('flex items-start gap-3 rounded-xl border p-3', VERDICT_TONE[queue.verdict.tone])}>
                    <div className="min-w-0 flex-1">
                        <p className="flex items-start gap-2 text-[13px] font-medium text-foreground">
                            <span
                                aria-hidden
                                className={cn('mt-1.5 size-1.5 shrink-0 rounded-full', VERDICT_DOT[queue.verdict.tone])}
                            />
                            <span className="min-w-0">{verdictText(t, queue.verdict)}</span>
                        </p>
                        <p className="mt-1 ps-3.5 text-xs text-muted-foreground">
                            {t('INSIGHTS.PROVENANCE.LEAD', { days: prov.days })}{' '}
                            {t('INSIGHTS.PROVENANCE.REST', { count: prov.checks })}
                        </p>
                    </div>

                    <div className="flex shrink-0 items-center gap-1">
                        {total > 0 && (
                            <Badge variant="secondary" className="px-1.5 py-0 text-[11px]">
                                {total}
                            </Badge>
                        )}
                        {showSeeAll && total > items.length && (
                            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setAllOpen(true)}>
                                {t('INSIGHTS.SEE_ALL')}
                            </Button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex items-center gap-2">
                    <h2 className="text-sm font-medium">{t('INSIGHTS.PANEL_TITLE')}</h2>
                    {total > 0 && (
                        <Badge variant="secondary" className="px-1.5 py-0 text-[11px]">
                            {total}
                        </Badge>
                    )}
                </div>
            )}

            {loading &&
                (isRow ? (
                    <div className="flex flex-col gap-3">
                        <Skeleton className="h-40 w-full" />
                        <Skeleton className="h-9 w-full" />
                        <Skeleton className="h-9 w-full" />
                    </div>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2">
                        {[1, 2, 3].map((i) => (
                            <Skeleton key={i} className="h-64" />
                        ))}
                    </div>
                ))}

            {!loading && items.length === 0 && <AllClear detectors={queue?.detectors ?? []} />}

            {!loading && items.length > 0 && (
                isRow ? (
                    /*
                     * The one that matters, then the rest as lines.
                     *
                     * Four cards sideways behind a scroll meant most of the
                     * queue was never seen. The lead finding is already the
                     * highest-scoring one — the engine sorts them — so it keeps
                     * a card, and everything after it costs a line and opens
                     * where there is room to read it.
                     */
                    <div className="flex flex-col gap-3">
                        <InsightCard
                            finding={items[0]}
                            onDismiss={dismiss}
                            onResolved={resolved}
                            busy={busyId === items[0].findingId}
                        />

                        {items.length > 1 && (
                            <div className="flex flex-col">
                                {items.slice(1).map((finding) => (
                                    <InsightRow
                                        key={finding.findingId}
                                        finding={finding}
                                        onOpen={() => setOpened(finding)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2">
                        {items.map((finding) => (
                            <InsightCard
                                key={finding.findingId}
                                finding={finding}
                                onDismiss={dismiss}
                                onResolved={resolved}
                                busy={busyId === finding.findingId}
                            />
                        ))}
                    </div>
                )
            )}

            {/* A row opened. The card is the whole finding, so the drawer shows
                the same component rather than a second telling of it. */}
            <SidePanel
                open={Boolean(opened)}
                onOpenChange={(next) => !next && setOpened(null)}
                title={t('INSIGHTS.TITLE')}
                width="data-[side=right]:sm:max-w-[560px]"
            >
                {opened && (
                    <InsightCard
                        finding={opened}
                        onDismiss={(id) => {
                            setOpened(null);
                            dismiss(id);
                        }}
                        onResolved={(id) => {
                            setOpened(null);
                            resolved(id);
                        }}
                        busy={busyId === opened.findingId}
                    />
                )}
            </SidePanel>

            {/* The rest of the queue. `showSeeAll={false}` on the inner panel
                is what stops this recursing — it is the same component, at a
                different length, exactly as the old page was. */}
            {showSeeAll && (
                <SidePanel
                    open={allOpen}
                    onOpenChange={setAllOpen}
                    title={t('INSIGHTS.TITLE')}
                    width="data-[side=right]:sm:max-w-[820px]"
                >
                    <InsightsPanel limit={20} showSeeAll={false} layout="grid" />
                </SidePanel>
            )}
        </section>
    );
};

/**
 * Nothing needs you — said with what was checked.
 *
 * An empty queue that says nothing looks like a broken queue, which is the
 * same failure the metrics layer refuses when it will not report a plausible
 * zero.
 */
const AllClear: React.FC<{ detectors: { kind: string; description: string }[] }> = ({ detectors }) => {
    const { t } = useTranslation();

    return (
        /*
         * Fills the column and centres, rather than sitting as a short notice
         * with a hole beneath it. `flex-1` is what does the filling — the
         * section is a column, so this takes whatever the cards on the right
         * leave.
         *
         * Solid border, not dashed: dashed reads as a placeholder or a drop
         * target, and this is a result. It is the one state on the dashboard
         * that is good news, so it gets to look settled rather than unfinished.
         */
        <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-xl border p-8 text-center">
            <span className="flex size-11 items-center justify-center rounded-full bg-success/10">
                <CircleCheck className="size-5 text-success" />
            </span>
            <div className="flex flex-col gap-1.5">
                <p className="text-[15px] font-medium">{t('INSIGHTS.EMPTY.TITLE')}</p>
                {/*
                  Still names what was checked. An empty queue that only says
                  "nothing here" is indistinguishable from a broken one — the
                  same reason the metrics layer refuses to report a plausible
                  zero.
                */}
                <p className="max-w-[46ch] text-[13px] leading-relaxed text-muted-foreground">
                    {t('INSIGHTS.EMPTY.BODY', { count: detectors.length })}
                </p>
            </div>
        </div>
    );
};
